import { useT } from '../../i18n';
import {
  useAddComment,
  useBabies,
  useDeleteComment,
  useEngagement,
  useMe,
  useToggleReaction,
} from '../../data/queries';
import type { EngagementEntityType } from '../../data/dto';
import { CommentComposer } from './CommentComposer';
import { CommentList } from './CommentList';
import { ReactionPicker } from './ReactionPicker';

interface Props {
  babyId: string;
  entityType: EngagementEntityType;
  entityId: string | number;
  /** Optional heading override; defaults to a generic "Comments & reactions". */
  className?: string;
}

/**
 * Self-contained comments + reactions block for any supported entity. Drop it
 * onto a detail surface with the baby id and the entity's (type, id). Handles
 * its own data fetching, optimistic-ish refresh, and permissions (anyone with
 * access can post; author/owner can delete).
 */
export function EngagementBar({ babyId, entityType, entityId, className = '' }: Props) {
  const t = useT();
  const engagementQ = useEngagement(babyId, entityType, entityId);
  const meQ = useMe();
  const babiesQ = useBabies();

  const addComment = useAddComment();
  const deleteComment = useDeleteComment();
  const toggleReaction = useToggleReaction();

  const currentUserId = meQ.data?.user.id ?? null;
  const myRole = (babiesQ.data ?? []).find((b) => b.id === babyId)?.role;
  const isOwner = myRole === 'owner';
  // Viewers are read-only: they can read comments/reactions but not post.
  const canInteract = myRole !== 'viewer';

  const data = engagementQ.data;
  const comments = data?.comments ?? [];

  const target = { babyId, entityType, entityId };

  return (
    <section className={['flex flex-col gap-3', className].join(' ')}>
      <h3 className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
        {t('engagement.title', 'Comments & reactions')}
      </h3>

      <ReactionPicker
        summary={data?.reactions ?? []}
        mine={data?.my_reaction ?? null}
        disabled={toggleReaction.isPending || !data}
        readOnly={!canInteract}
        onToggle={(emoji) => toggleReaction.mutate({ ...target, emoji })}
      />

      {comments.length > 0 && (
        <CommentList
          comments={comments}
          currentUserId={currentUserId}
          isOwner={isOwner}
          onDelete={(commentId) => deleteComment.mutate({ ...target, commentId })}
          deletingId={deleteComment.isPending ? deleteComment.variables?.commentId ?? null : null}
        />
      )}

      {engagementQ.isLoading && (
        <p className="text-[13px] text-ink-3">{t('common.loading', 'Loading…')}</p>
      )}

      {canInteract && (
        <CommentComposer
          submitting={addComment.isPending}
          onSubmit={(bodyHtml) => addComment.mutate({ ...target, bodyHtml })}
        />
      )}
    </section>
  );
}
